import React from 'react';
import {
  Printer,
  Download,
  CheckCircle2,
  Send,
  Sparkles,
  Shield,
  Clock,
  Calendar,
  MapPin,
  Compass,
  FileText,
  User
} from 'lucide-react';
import { PracticeReport, ReportStatus } from '../../types/practiceManagement';
import { Language, CompleteKundali, ChartStyle } from '../../types/kundali';
import { getPracticeSettings } from '../../lib/practiceManagementService';

interface ReportPreviewProps {
  report: PracticeReport;
  kundali?: CompleteKundali;
  chartStyle?: ChartStyle;
  lang: Language;
  onApproveReport?: (reportId: string) => void;
  onDeliverReport?: (reportId: string) => void;
}

export const ReportPreview: React.FC<ReportPreviewProps> = ({
  report,
  kundali,
  chartStyle = 'north',
  lang,
  onApproveReport,
  onDeliverReport
}) => {
  const isHi = lang === 'hi';
  const settings = getPracticeSettings();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4">
      {/* Top Action Bar */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-xs text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.5 rounded border border-[#E2DED5] dark:border-amber-800">
            {report.reportNumber}
          </span>
          <span className="text-xs font-bold text-astro-text dark:text-astro-muted">
            Version {report.version}
          </span>
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              report.status === 'DELIVERED'
                ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                : report.status === 'APPROVED'
                ? 'bg-blue-500/15 text-blue-800 dark:text-blue-300'
                : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300'
            }`}
          >
            {report.status}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {report.status === 'READY_FOR_REVIEW' && onApproveReport && (
            <button
              onClick={() => onApproveReport(report.id)}
              className="px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{isHi ? 'स्वीकृत करें (Approve)' : 'Approve Report'}</span>
            </button>
          )}

          {report.status === 'APPROVED' && onDeliverReport && (
            <button
              onClick={() => onDeliverReport(report.id)}
              className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isHi ? 'क्लाइंट को भेजें (Deliver)' : 'Mark Delivered'}</span>
            </button>
          )}

          <button
            onClick={handlePrint}
            className="min-h-[38px] px-4 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
          >
            <Printer className="w-4 h-4" />
            <span>{isHi ? 'प्रिंट / PDF एक्सपोर्ट' : 'Print / Export PDF'}</span>
          </button>
        </div>
      </div>

      {/* Live A4 Sheet Preview Container */}
      <div className="bg-astro-secondary-surface dark:bg-astro-primary/80 p-4 md:p-8 rounded-2xl flex justify-center overflow-x-auto">
        <div
          id="official-vedic-report-document"
          className="w-full max-w-[800px] min-h-[1130px] bg-white text-astro-text p-8 md:p-12 rounded-xl shadow-xl border border-astro-border/90 space-y-6 font-sans select-text"
        >
          {/* Practice Letterhead Header */}
          {report.config.includeCover && (
            <div className="border-b-2 border-amber-600/60 pb-6 flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <h1 className="text-xl font-black tracking-tight text-astro-text font-serif">
                    {settings.practiceName}
                  </h1>
                </div>
                <p className="text-xs text-astro-secondary-text font-serif italic">{settings.tagline}</p>
                <p className="text-[11px] text-astro-muted">
                  {settings.traditionLineage} • {settings.location}
                </p>
              </div>

              <div className="text-right text-xs space-y-1">
                <div className="font-mono font-bold text-astro-secondary bg-astro-secondary-surface px-2 py-0.5 rounded border border-[#E2DED5] inline-block">
                  {report.reportNumber}
                </div>
                <div className="text-astro-muted font-mono text-[11px]">
                  Date: {report.generatedDate}
                </div>
                <div className="text-astro-secondary-text font-bold text-[11px]">
                  Ref: {report.clientRefId}
                </div>
              </div>
            </div>
          )}

          {/* Report Title & Subject */}
          <div className="space-y-1.5 pt-2">
            <div className="text-[11px] font-bold tracking-wider text-astro-secondary uppercase">
              {report.reportType.replace(/_/g, ' ')}
            </div>
            <h2 className="text-lg font-black text-astro-text font-serif leading-snug">
              {report.title}
            </h2>
            <p className="text-xs text-astro-secondary-text leading-relaxed italic">{report.subtitle}</p>
          </div>

          {/* Client & Natal Profile Box */}
          {report.config.includeBirthCoordinates && (
            <div className="bg-astro-secondary-surface p-4 rounded-xl border border-astro-border grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-astro-muted block text-[10px] uppercase font-bold">Client Name</span>
                <span className="font-bold text-astro-text">{report.clientName}</span>
              </div>
              <div>
                <span className="text-astro-muted block text-[10px] uppercase font-bold">Client Ref ID</span>
                <span className="font-mono font-bold text-astro-text">{report.clientRefId}</span>
              </div>
              <div>
                <span className="text-astro-muted block text-[10px] uppercase font-bold">Generated By</span>
                <span className="font-semibold text-astro-text">{report.generatedBy}</span>
              </div>
              <div>
                <span className="text-astro-muted block text-[10px] uppercase font-bold">Target Horizon</span>
                <span className="font-bold text-astro-secondary">2026 – 2027</span>
              </div>
            </div>
          )}

          {/* Primary Consultation Target Question */}
          <div className="p-4 bg-astro-secondary-surface/80 rounded-xl border border-[#E2DED5]/90 text-xs space-y-1">
            <span className="font-bold text-astro-secondary uppercase text-[10px] tracking-wider block">
              Consultation Inquiry & Focal Question:
            </span>
            <p className="font-semibold text-astro-text italic text-sm">
              "{report.targetQuestion}"
            </p>
          </div>

          {/* Executive Summary */}
          {report.config.includeNatalPromise && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text border-b border-astro-border pb-1 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                <span>1. Astrological Synthesis & Natal Promise</span>
              </h3>
              <p className="text-xs text-astro-text leading-relaxed text-justify">
                {report.executiveSummaryText}
              </p>
            </div>
          )}

          {/* Embedded Charts Thumbnail / Planetary Placements if present */}
          {report.config.includeD1Chart && kundali && (
            <div className="space-y-2 break-inside-avoid">
              <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text border-b border-astro-border pb-1 flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5 text-amber-600" />
                <span>2. Core Rashi (D1) Geometry & Placements</span>
              </h3>
              <div className="p-3 bg-astro-secondary-surface rounded-xl border border-astro-border text-xs">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {kundali.planets?.map((p) => (
                    <div key={p.name} className="p-2 rounded bg-white border border-astro-border/80">
                      <span className="text-[10px] text-astro-muted font-bold block uppercase">{p.name}</span>
                      <span className="font-semibold text-astro-text">{p.sign} {p.degreeFormatted || `${Math.floor(p.degreeInSign || 0)}°`}</span>
                      <span className="text-[9px] text-astro-muted block">{p.nakshatra}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Phala-Bhuta Timing Windows */}
          {report.config.includePhalaBhutaWindows && (
            <div className="space-y-2 break-inside-avoid">
              <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text border-b border-astro-border pb-1 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-600" />
                <span>3. Phala-Bhuta Timing Windows (Dasha-Gochar Confluence)</span>
              </h3>
              <div className="p-3 bg-astro-secondary-surface/60 rounded-xl border border-[#E2DED5] text-xs font-semibold text-[#263633]">
                {report.timingVerdictText}
              </div>
            </div>
          )}

          {/* Prescribed Remedies */}
          {report.config.includeRemedies && (
            <div className="space-y-2 break-inside-avoid">
              <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text border-b border-astro-border pb-1 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-amber-600" />
                <span>4. Prescribed Classical Remedies & Guidance</span>
              </h3>
              <div className="p-3.5 bg-astro-secondary-surface rounded-xl border border-astro-border text-xs text-astro-text space-y-1.5 whitespace-pre-line leading-relaxed">
                {report.remediesText}
              </div>
            </div>
          )}

          {/* Practitioner Signature & Seal */}
          <div className="pt-8 border-t border-astro-border flex items-end justify-between gap-4 break-inside-avoid">
            <div className="text-[10px] text-astro-muted max-w-sm space-y-1">
              <p className="font-semibold text-astro-secondary-text">Official Disclaimer:</p>
              <p className="leading-tight">{settings.officialDisclaimerText}</p>
            </div>

            <div className="text-right space-y-2">
              <div className="font-serif italic font-bold text-sm text-astro-text">
                {settings.signatureText}
              </div>
              <div className="text-[10px] text-astro-muted uppercase tracking-wider">
                {report.practitionerCredentials || 'Principal Vedic Astrologer'}
              </div>
              <div className="text-[10px] font-mono text-astro-muted">
                Seal & Auth: {report.reportNumber}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
